import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCancelUserParkingPermitMutation } from "@features/userDetails/userDetailsApi.ts";
import { ParkingPermit } from "@features/userDetails/types.ts";
import dayjs, { TIMEZONE } from "@utils/dayjs.ts";

export default function EndPermit({ permit }: { permit: ParkingPermit }) {
  const { t } = useTranslation();

  const [cancelUserParkingPermit, mutation] =
    useCancelUserParkingPermitMutation();
  const [open, setOpen] = useState(false);

  const handleClose = useCallback(() => {
    mutation.reset();
    setOpen(false);
  }, [mutation]);

  const onPermitCancel = (permitId: string) => {
    void cancelUserParkingPermit({ id: permitId });
  };

  function handlePermitCancel(permitId: string) {
    onPermitCancel(permitId);
  }

  useEffect(() => {
    if (mutation.isSuccess) {
      handleClose();
    }
  }, [mutation.isSuccess, handleClose]);

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t("userDetailsPage.parkingPermits.cancel")}
      </Button>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {t("userDetailsPage.parkingPermits.cancelConfirmationTitle")}
        </DialogTitle>
        <DialogContent>
          {mutation.isError && (
            <Alert severity="error">
              {t("userDetailsPage.parkingPermits.cancelError")}
            </Alert>
          )}
          <DialogContentText>
            {t("userDetailsPage.parkingPermits.cancelConfirmationText")}
          </DialogContentText>
          {permit.paid_until && (
            <DialogContentText mt="1rem">
              {t(
                "userDetailsPage.parkingPermits.cancelConfirmationTextPaidUntil",
                {
                  paid_until: dayjs(permit.paid_until)
                    .tz(TIMEZONE)
                    .format("LLL"),
                },
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={mutation.isLoading}>
            {t("userDetailsPage.parkingPermits.closeButton")}
          </Button>
          <Button
            onClick={() => {
              handlePermitCancel(permit.id);
            }}
            color="error"
            loading={mutation.isLoading}
          >
            {t("userDetailsPage.parkingPermits.confirmButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
